<script lang="ts">
  import Icon from '../components/Icon.svelte';
  import { MATERIAL_TYPE_ICONS } from '../lib/icons';
  import { onMount } from 'svelte';
  import { replace } from 'svelte-spa-router';
  import TopBar from '../components/TopBar.svelte';
  import { session, applySession } from '../lib/session';
  import { adminMode } from '../lib/admin-mode';
  import { attachPlaybackTracking } from '../lib/playback';
  import { attachTextProgressTracking } from '../lib/text-progress';
  import UpsellBlock from '../components/UpsellBlock.svelte';
  import HeartButton from '../components/HeartButton.svelte';
  import CommentsSection from '../components/CommentsSection.svelte';
  import AudioPlayer from '../components/AudioPlayer.svelte';
  import VideoPlayer from '../components/player/VideoPlayer.svelte';
  import type { PlayerSettings } from '../lib/player/settings';
  import Lightbox from '../components/Lightbox.svelte';
  import { api, ApiError } from '../lib/api';
  import { getInitData, isInsideTelegram } from '../lib/telegram';
  import { formatDate } from '../lib/format';
  import { renderMarkdown } from '../lib/markdown';
  import type { MaterialDetailDTO, MaterialType } from '../lib/types';

  interface Props {
    params?: { id?: string };
  }
  let { params }: Props = $props();
  const materialId = $derived(params?.id ?? '');

  let material = $state<MaterialDetailDTO | null>(null);
  let notFound = $state(false);
  let error = $state<string | null>(null);
  let playbackUrl = $state<string | null>(null);
  // Video: direct HLS source for our own player (absent → Bunny iframe fallback).
  let hls = $state<{ url: string; poster: string | null; startAt: number; player?: PlayerSettings } | null>(
    null,
  );
  let playbackLoading = $state(false);
  let playbackError = $state<string | null>(null); // temporary: surfaced in the placeholder for diagnosis
  let playbackTried = false;
  let iframeEl = $state<HTMLIFrameElement | undefined>(undefined);
  let scrollEl = $state<HTMLDivElement | undefined>(undefined);
  // Сама стаття всередині .scroll. Потрібна трекеру прогресу: під нею тепер
  // живуть коментарі, і без цього посилання «прочитано» рахувалося б по висоті
  // разом із чужими репліками.
  let articleEl = $state<HTMLDivElement | undefined>(undefined);
  let detachTracking: (() => void) | null = null;
  let detachTextTracking: (() => void) | null = null;

  // Lightbox state — non-null = open, holds the image list + which one to show
  // first. Used for both single-image blocks (one-item array) and galleries.
  let lightbox = $state<{ images: string[]; index: number } | null>(null);
  function openLightbox(images: string[], index: number): void {
    lightbox = { images, index };
  }

  // Per-material access is resolved by the backend (the material's category is
  // unlocked by one of the user's products; admins + Club owners always true)
  // and shipped on the DTO. No client-side subscription logic — the frontend
  // just renders what the server already decided. Body/textContent are stripped
  // server-side when hasAccess is false, so this only toggles the lock UI.
  const canSee = $derived(material?.hasAccess ?? false);

  // Admin acting in editor mode — preview, no progress tracking, edit FAB visible.
  // In user mode an admin behaves like a regular subscriber for all UX purposes.
  const adminEditing = $derived(($session?.isAdmin ?? false) && $adminMode === 'admin');

  // Iframe fallback only (our VideoPlayer reports progress itself): wire up
  // player.js tracking once the iframe has rendered + URL is set.
  // Admins in editor mode skip tracking — they're previewing, not consuming.
  // Audio uses AudioPlayer which embeds its own tracking, so we only attach
  // here for type='video'.
  $effect(() => {
    if (!iframeEl || !playbackUrl || !material) return;
    if (adminEditing) return;
    if (material.type !== 'video') return;
    const handleLoad = (): void => {
      if (!material) return;
      detachTracking?.();
      detachTracking = attachPlaybackTracking(iframeEl!, material.id);
    };
    iframeEl.addEventListener('load', handleLoad);
    return () => {
      iframeEl?.removeEventListener('load', handleLoad);
      detachTracking?.();
      detachTracking = null;
    };
  });

  // Wire up scroll-depth tracking for text materials. RAF gives the browser
  // one frame to lay out the body blocks before we measure scrollHeight.
  // Admins in editor mode skip tracking — preview-only.
  $effect(() => {
    if (!scrollEl || !articleEl || !material || material.type !== 'text') return;
    if (!canSee) return;
    if (adminEditing) return;

    const raf = requestAnimationFrame(() => {
      if (!scrollEl || !material || !articleEl) return;
      detachTextTracking?.();
      detachTextTracking = attachTextProgressTracking(scrollEl, material.id, articleEl);
    });
    return () => {
      cancelAnimationFrame(raf);
      detachTextTracking?.();
      detachTextTracking = null;
    };
  });

  onMount(async () => {
    // Refresh session in background (non-blocking)
    (async () => {
      try {
        if (isInsideTelegram()) applySession(await api.authMe(getInitData()));
        else applySession(await api.authSession());
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          replace(import.meta.env.DEV ? '/' : '/login');
        }
      }
    })();

    if (!materialId) {
      notFound = true;
      return;
    }
    try {
      material = await api.material(materialId);
    } catch (err) {
      if (err instanceof ApiError && (err.status === 404 || err.status === 400)) {
        notFound = true;
      } else {
        error = 'Не вдалось завантажити матеріал';
      }
      return;
    }

    // (Heart toggle defined below; logic stays the same regardless of type)
    // Playback URL is fetched reactively in the $effect below — once both the
    // material AND the session (canSee) are ready. Doing it here in onMount
    // raced: auth goes through SendPulse and often resolves AFTER the local
    // material fetch, so canSee was still false and playback was skipped.
  });

  // Reactive playback fetch: fires as soon as material + access are both ready,
  // regardless of which resolved first. `playbackTried` keeps it to one call.
  $effect(() => {
    if (playbackTried) return;
    if (!material) return;
    if (material.type !== 'video' && material.type !== 'audio') return;
    if (!canSee) return;
    playbackTried = true;
    const id = material.id;
    playbackLoading = true;
    api
      .materialPlayback(id)
      .then((r) => {
        playbackUrl = r.iframeUrl;
        if (r.hlsUrl) hls = { url: r.hlsUrl, poster: r.posterUrl ?? null, startAt: r.startAt ?? 0, player: r.player };
      })
      .catch((e) => {
        playbackError = e instanceof ApiError ? `${e.status}:${e.code}` : 'network';
      })
      .finally(() => {
        playbackLoading = false;
      });
  });

  function saveVideoProgress(sec: number, duration: number): void {
    if (!material) return;
    api.postProgress(material.id, { sec, duration }).catch(() => {});
  }

  /** Optimistic toggle — flip locally, call API, revert on error. */
  async function toggleFavorite(): Promise<void> {
    if (!material) return;
    const before = material.isFavorite;
    material = { ...material, isFavorite: !before };
    try {
      const { isFavorite } = await api.toggleFavorite(material.id);
      material = { ...material, isFavorite };
    } catch {
      material = { ...material, isFavorite: before };
    }
  }
</script>

<div class="screen">
  <!-- backHref is only the cold-landing fallback (e.g. a deep link straight to
       this material); normal in-app back uses history. Send cold opens to База
       знань rather than a section the user never visited. -->
  <TopBar
    title={material?.category.name ?? 'Матеріал'}
    backHref="/catalog"
    showLogo={false}
  />
  <div class="scroll" bind:this={scrollEl}>
    {#if notFound}
      <div class="empty">Матеріал не знайдено</div>
    {:else if error}
      <div class="empty">{error}</div>
    {:else if material}
      <div
        class="card-list no-copy"
        bind:this={articleEl}
        oncopy={(e) => e.preventDefault()}
        oncut={(e) => e.preventDefault()}
        oncontextmenu={(e) => e.preventDefault()}
      >
        <div class="title-row">
          <h1 class="title">{material.title}</h1>
          {#if !adminEditing}
            <HeartButton isFavorite={material.isFavorite} onToggle={toggleFavorite} size={28} />
          {/if}
        </div>

        {#if material.description}
          <div class="description md-content">{@html renderMarkdown(material.description)}</div>
        {/if}

        {#if canSee}
          <!-- Main media first for video/audio (the primary content), then
               body blocks below as transcript / supplementary text. Text-only
               materials skip the player branch entirely and show blocks only. -->
          {#if material.type === 'audio' && playbackUrl}
            <AudioPlayer
              src={playbackUrl}
              materialId={material.id}
              trackProgress={!adminEditing}
              chapters={material.chapters}
              title={material.title}
              artist={material.category.name}
            />
          {:else if material.type === 'video' && hls}
            <VideoPlayer
              hlsUrl={hls.url}
              posterUrl={hls.poster}
              startAt={adminEditing ? 0 : hls.startAt}
              title={material.title}
              settings={hls.player}
              onProgress={adminEditing ? undefined : saveVideoProgress}
            />
          {:else if material.type === 'video' && playbackUrl}
            <div class="player-wrap">
              <iframe
                bind:this={iframeEl}
                src={playbackUrl}
                title={material.title}
                allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture; fullscreen"
                allowfullscreen
              ></iframe>
            </div>
          {:else if (material.type === 'video' || material.type === 'audio') && playbackLoading}
            <div class="placeholder">
              <div class="placeholder-text">Завантажуємо плеєр…</div>
            </div>
          {:else if material.type === 'video' || material.type === 'audio'}
            <div class="placeholder">
              <div class="placeholder-icon"><Icon name={MATERIAL_TYPE_ICONS[material.type]} /></div>
              <div class="placeholder-text">Цей матеріал поки не доступний</div>
              {#if playbackError}
                <div class="placeholder-debug">debug: {playbackError} · sess:{$session ? 'y' : 'n'} · see:{canSee ? 'y' : 'n'}</div>
              {/if}
            </div>
          {/if}

          {#if material.body.length > 0}
            <div class="blocks">
              {#each material.body as block (block.id)}
                {#if block.type === 'text'}
                  <div class="text-content md-content">{@html renderMarkdown(block.markdown)}</div>
                {:else if block.type === 'image' && block.url}
                  <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_noninteractive_element_interactions -->
                  <img
                    class="material-cover clickable width-{block.width ?? 'full'}"
                    src={block.url}
                    alt=""
                    onclick={() => openLightbox([block.url], 0)}
                  />
                {:else if block.type === 'gallery' && block.urls.length > 0}
                  <div class="material-media-group cols-{block.cols && block.urls.length >= block.cols ? block.cols : 2}">
                    {#each block.urls as src, i (src + i)}
                      <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_noninteractive_element_interactions -->
                      <img
                        class="clickable"
                        src={src}
                        alt=""
                        onclick={() => openLightbox(block.urls, i)}
                      />
                    {/each}
                  </div>
                {/if}
              {/each}
            </div>
          {/if}
        {:else}
          <UpsellBlock
            title="Матеріал поки закритий"
            text="Для доступу до цього матеріалу активуйте підписку."
          />
        {/if}

        {#if material.tags.length > 0}
          <div class="tags">
            {#each material.tags as tag}
              <span class="tag">#{tag}</span>
            {/each}
          </div>
        {/if}

        {#if material.publishedAt}
          <div class="published">
            Опубліковано {formatDate(material.publishedAt)}
          </div>
        {/if}
      </div>

      <!-- Поза .card-list навмисно: клас .no-copy ставить user-select: none на
           всіх нащадків і глушить contextmenu, через що всередині нього
           <textarea> втрачає каретку й меню «Вставити» в Telegram WebView.
           Заразом це лишає вимірювання прогресу прив'язаним до самої статті. -->
      {#if canSee}
        <CommentsSection materialId={material.id} canWrite={!adminEditing} />
      {/if}
    {/if}
  </div>

  {#if adminEditing && material}
    <a class="admin-edit-fab" href={`#/admin/material/${material.id}/edit`} aria-label="Редагувати">
      <span class="edit-icon"><Icon name="square-pen" /></span> Редагувати
    </a>
  {/if}

  {#if lightbox}
    <Lightbox
      images={lightbox.images}
      initialIndex={lightbox.index}
      onClose={() => (lightbox = null)}
    />
  {/if}
</div>

<style>
  /* Floating admin-only edit shortcut on the material preview page. */
  .admin-edit-fab {
    position: fixed;
    right: 16px;
    bottom: calc(76px + env(safe-area-inset-bottom));
    background: linear-gradient(135deg, var(--dark3), var(--pu3));
    color: var(--text-light);
    padding: 11px 16px;
    border-radius: 999px;
    font-size: 13px;
    font-weight: 600;
    text-decoration: none;
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
    z-index: 50;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .edit-icon {
    display: inline-block;
    transform: scaleX(-1);
  }

  .screen {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    background: var(--bg);
  }
  .scroll {
    flex: 1;
    overflow-y: auto;
    padding: 18px 18px 100px;
  }
  .scroll::-webkit-scrollbar { width: 3px; }
  .scroll::-webkit-scrollbar-thumb { background: var(--border2); border-radius: 2px; }

  /* Block layout — vertical stack inside .blocks with consistent gap between
   *   text/image/gallery blocks. Each block keeps its own internal styles. */
  .blocks {
    display: flex;
    flex-direction: column;
    gap: 14px;
    margin-bottom: 14px;
  }
  .blocks .text-content,
  .blocks .material-cover,
  .blocks .material-media-group {
    margin-bottom: 0;
  }
  .material-cover {
    width: 100%;
    height: auto;
    border-radius: 12px;
    display: block;
  }
  /* Per-block width override — `medium` and `small` center horizontally so the
   * image sits in the middle of the column instead of hugging the left edge. */
  .material-cover.width-medium {
    width: 70%;
    margin-left: auto;
    margin-right: auto;
  }
  .material-cover.width-small {
    width: 45%;
    margin-left: auto;
    margin-right: auto;
  }
  .material-media-group {
    display: grid;
    gap: 8px;
  }
  .material-media-group.cols-2 { grid-template-columns: repeat(2, 1fr); }
  .material-media-group.cols-3 { grid-template-columns: repeat(3, 1fr); }
  .material-media-group.cols-4 { grid-template-columns: repeat(4, 1fr); }
  .material-media-group img {
    width: 100%;
    aspect-ratio: 1;
    object-fit: cover;
    border-radius: 10px;
    display: block;
  }
  /* Visual cue + tap-friendly cursor for clickable images (opens lightbox). */
  .clickable {
    cursor: zoom-in;
    transition: opacity 0.15s;
  }
  .clickable:active {
    opacity: 0.85;
  }

  .title-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 16px;
  }
  .title {
    font-size: 22px;
    font-weight: 700;
    color: var(--text);
    line-height: 1.3;
    margin: 0;
    flex: 1;
    min-width: 0;
  }

  .text-content {
    font-size: 14px;
    line-height: 1.7;
    color: var(--text);
    margin-bottom: 18px;
    padding: 16px;
    background: var(--bg2);
    border: 1px solid var(--border);
    border-radius: 12px;
  }

  .player-wrap {
    position: relative;
    width: 100%;
    aspect-ratio: 16 / 9;
    border-radius: 12px;
    overflow: hidden;
    background: black;
    margin-bottom: 18px;
  }
  .player-wrap iframe {
    width: 100%;
    height: 100%;
    border: 0;
    display: block;
  }
  .placeholder {
    padding: 40px 20px;
    text-align: center;
    background: var(--bg2);
    border: 1px dashed var(--border2);
    border-radius: 12px;
    margin-bottom: 18px;
  }
  .placeholder-icon {
    font-size: 36px;
    margin-bottom: 8px;
  }
  .placeholder-text {
    font-size: 12px;
    color: var(--text-muted);
  }
  .placeholder-debug {
    margin-top: 8px;
    font-size: 11px;
    font-family: monospace;
    color: var(--status-inactive);
    word-break: break-all;
  }

  .description {
    font-size: 13px;
    line-height: 1.6;
    color: var(--text-muted);
    margin-bottom: 14px;
  }

  .tags {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 14px;
  }
  .tag {
    font-size: 11px;
    color: var(--accent-text);
    background: var(--pu-light);
    padding: 3px 9px;
    border-radius: 999px;
  }

  .published {
    font-size: 11px;
    color: var(--text-muted);
    text-align: center;
    padding: 12px;
    margin-top: 8px;
  }

  .empty {
    text-align: center;
    padding: 60px 20px;
    color: var(--text-muted);
    font-size: 13px;
  }
</style>
